import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { MdDelete } from "react-icons/md";
import { storage } from "../../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  addNewDriver,
  editDriver,
  fetchCompany,
  fetchDriverById,
} from "../../store/slices/thunk";
import { toast, ToastContainer } from "react-toastify";
import {
  Autocomplete as GMapAutocomplete,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { getTenantConfigFromStorage } from "../../helpers/tenantConfig";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    height: "46px",
    "& fieldset": { borderColor: "#d0d5dd" },
    "&:hover fieldset": { borderColor: "#7c6dff" },
    "&.Mui-focused fieldset": { borderColor: "#7c6dff" },
  },
};

const selectSx = {
  ...textFieldSx,
  "& .MuiOutlinedInput-root": {
    ...textFieldSx["& .MuiOutlinedInput-root"],
    height: "46px",
  },
  "& .MuiMenuItem-root": {
    color: "#000",
  },
};

const FormLabel = ({ label, required }: { label: string; required?: boolean }) => (
  <Typography
    variant="body2"
    sx={{
      fontWeight: 600,
      color: "#344054",
      fontSize: "0.85rem",
      mb: 0.75,
    }}
  >
    {label}
    {required && <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>*</Box>}
  </Typography>
);

const renderField = (
  label: string,
  required: boolean,
  field: React.ReactNode
) => (
  <Box>
    <FormLabel label={label} required={required} />
    {field}
  </Box>
);

const UploadBox = ({
  icon,
  title,
  subtitle,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick?: () => void;
  disabled?: boolean;
}) => (
  <Box
    onClick={onClick}
    sx={{
      border: "2px dashed #d0d5dd",
      borderRadius: "12px",
      p: 3,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: onClick ? "pointer" : "default",
      backgroundColor: "#fafbfc",
      minHeight: 100,
      transition: "all 0.2s",
      opacity: disabled ? 0.6 : 1,
      "&:hover": onClick
        ? {
            borderColor: "#7c6dff",
            backgroundColor: "#f5f3ff",
          }
        : {},
    }}
  >
    <Box sx={{ mb: 1, color: "#7c6dff" }}>{icon}</Box>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: "#344054", fontSize: "0.8rem" }}
    >
      {title}
    </Typography>
    <Typography variant="caption" sx={{ color: "#98a2b3", fontSize: "0.7rem" }}>
      {subtitle}
    </Typography>
  </Box>
);

const Addriver = () => {
  const tenantConfig = getTenantConfigFromStorage();
  const schoolLabel = tenantConfig.labels.school;
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [documentProgress, setDocumentProgress] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [mapSrc, setMapSrc] = useState("");

  const [addDriver, setAddDriver] = useState({
    driverName: "",
    email: "",
    phoneNumber: "",
    age: 0,
    licenseNo: "",
    licenseExpiryDate: 0,
    totalExperiance: "",
    dateOfJoining: 0,
    reference: "",
    address: {
      coordinates: [],
      type: "Point",
      streetAddress: "",
    },
    driverStatus: "Active",
    driverImage: "",
    driverDocument: "",
    company: {
      companyId: "",
      companyName: "",
    },
    _id: "",
  });
  const dispatch: any = useDispatch();

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places", "geometry"],
  });
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const { id } = useParams<any>();
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [selectedPosition, setSelectedPosition] = useState<any>(null);
  const onLoadAutocomplete = (auto: any) => {
    setAutocomplete(auto);
  };
  const [searchCompany, setSearchCompany] = React.useState("");
  const [companyId, setCompanyId] = useState<string>("");
  const [allCompany, setAllCompany] = useState<any>([]);
  const [autocomplete, setAutocomplete] = useState<any>(null);
  const [locationObject, setLocationObject] = useState<any>(null);

  useEffect(() => {
    const getAllCompany = async () => {
      const response: any = await dispatch(
        fetchCompany(1, 99999999, searchCompany, "")
      );
      if (response && response?.data?.data?.length > 0 && response?.isSuccess) {
        setAllCompany(response?.data?.data);
      } else {
        setAllCompany([]);
      }
    };
    getAllCompany();
  }, [dispatch]);

  const handleSelectSuggestion = (place: any) => {
    setAddDriver((prev: any) => ({
      ...prev,
      address: {
        type: "Point",
        streetAddress: place.display_name,
        coordinates: [parseFloat(place.lon), parseFloat(place.lat)],
      },
    }));
    setSuggestions([]);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setIsEdit(true);
        const response: any = await dispatch(fetchDriverById(id));
        setAddDriver(response?.data);
        setSelectedPosition({
          lat: response?.data?.address?.coordinates[1],
          lng: response?.data?.address?.coordinates[0],
        });
        setLocationObject({
          lat: response?.data?.address?.coordinates[1],
          lng: response?.data?.address?.coordinates[0],
          streetAddress: response?.data?.address?.streetAddress,
        });
        setUploadProgress(0);
        navigate(`/edit-driver/${id}`);
      } else {
        setIsEdit(false);
        setAddDriver({
          driverName: "",
          email: "",
          phoneNumber: "",
          age: 0,
          licenseNo: "99",
          licenseExpiryDate: 0,
          totalExperiance: "",
          dateOfJoining: 0,
          reference: "",
          address: {
            coordinates: [],
            type: "Point",
            streetAddress: "",
          },
          driverStatus: "Active",
          driverImage: "",
          driverDocument: "",
          company: {
            companyId: "",
            companyName: "",
          },
          _id: "",
        });
        navigate("/add-driver");
      }
    };
    fetchData();
  }, [id, navigate, dispatch]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const {
        driverName,
        email,
        phoneNumber,
        age,
        licenseNo,
        totalExperiance,
        dateOfJoining,
        address,
        driverStatus,
        driverImage,
        driverDocument,
        company,
      } = addDriver;

      if (
        driverName === "" ||
        email === "" ||
        phoneNumber === "" ||
        age === 0 ||
        licenseNo === "" ||
        totalExperiance === "" ||
        dateOfJoining === 0 ||
        address.streetAddress === "" ||
        driverStatus === "" ||
        driverImage === "" ||
        driverDocument === "" ||
        company.companyId === ""
      ) {
        toast.error("Please fill all the required fields!");
        setLoading(false);
        return;
      }

      let res: any;
      if (isEdit) {
        res = await dispatch(editDriver(addDriver));
        toast("Driver Edited!");
        navigate("/driver-list");
      } else {
        res = await dispatch(addNewDriver(addDriver));
        if (res.data && res.isSuccess) {
          toast("Driver added!");
          navigate("/driver-list");
        } else {
          toast("Driver Not added!");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const navigateToDriverList = () => {
    navigate("/driver-list");
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setAddDriver((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [field]: value,
        },
      }));

      if (field === "streetAddress") {
        if (typingTimeout) clearTimeout(typingTimeout);
        const timeout = setTimeout(() => {
          if (value.trim().length > 2) {
            fetch(
              `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=pk&q=${encodeURIComponent(
                value
              )}`
            )
              .then((res) => res.json())
              .then((data) => {
                setSuggestions(data);
              })
              .catch((err) =>
                console.error("Error fetching suggestions:", err)
              );
          } else {
            setSuggestions([]);
          }
        }, 400);
        setTypingTimeout(timeout);
      }
    } else {
      setAddDriver((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingImage(true);

    const fileRef = ref(storage, `drivers/images/${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Upload error:", error);
        setIsUploadingImage(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref)
          .then((url) => {
            setAddDriver((prev) => ({
              ...prev,
              driverImage: url,
            }));
          })
          .finally(() => {
            setIsUploadingImage(false);
          });
      }
    );
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingDocument(true);

    const fileRef = ref(storage, `drivers/documents/${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setDocumentProgress(progress);
      },
      (error) => {
        console.error("Document Upload Error:", error);
        setIsUploadingDocument(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref)
          .then((url) => {
            setAddDriver((prev) => ({
              ...prev,
              driverDocument: url,
            }));
          })
          .finally(() => {
            setIsUploadingDocument(false);
          });
      }
    );
  };

  const handleRemoveDriverImage = () => {
    setAddDriver((prev) => ({
      ...prev,
      driverImage: "",
    }));
    setUploadProgress(0);
  };

  const handleRemoveDriverDocument = () => {
    setAddDriver((prev) => ({
      ...prev,
      driverDocument: "",
    }));
    setDocumentProgress(0);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      const lat = place.geometry?.location?.lat();
      const lng = place.geometry?.location?.lng();

      if (lat && lng) {
        setLocationObject({
          lat,
          lng,
          streetAddress: place.formatted_address || place.name || "",
        });
        setAddDriver((prevData: any) => ({
          ...prevData,
          address: {
            streetAddress: place.formatted_address || place.name || "",
            coordinates: [lng, lat],
            type: "Point",
          },
        }));
        setSelectedPosition({ lat, lng });
      }
    }
  };

  const handleChangeForCompany = (e: any) => {
    const { value } = e.target;
    setCompanyId(value);
    if (!value) {
      setAddDriver({
        ...addDriver,
        company: {
          companyId: "",
          companyName: "",
        },
      });
      return;
    }
    const selectedCompany: any = allCompany.find(
      (company: any) => company._id === value
    );
    if (selectedCompany) {
      setAddDriver({
        ...addDriver,
        company: {
          companyId: selectedCompany?._id,
          companyName: selectedCompany?.companyName,
        },
      });
    }
  };

  if (loadError) return <div>Map load error: {String(loadError)}</div>;
  if (!isLoaded) return <div>Loading Google Maps...</div>;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "transparent",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: -50,
          right: -50,
          width: 500,
          height: 500,
          borderRadius: "50%",
          pointerEvents: "none",
        },
      }}
    >
      <ToastContainer />
      {/* Header - Outside Card */}
      <Box sx={{ maxWidth: 1900, mx: "auto", mt: 4, mb: 2, px: 0, display: "flex", alignItems: "center", gap: 1.5, position: "relative", zIndex: 1 }}>
        <IconButton onClick={navigateToDriverList} sx={{ color: "#555", "&:hover": { backgroundColor: "#f5f5f5" } }}>
          <ArrowBackIcon sx={{ fontSize: 20,px: 0 }} />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e90ff" }}>
            {isEdit ? "Edit Driver" : "Add New Driver"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Fill in the details to add a new driver to your fleet.
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          maxWidth: 1900,
          mx: "auto",
          mb: 4,
          borderRadius: 4,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          border: "1px solid #eef0f5",
          position: "relative",
          zIndex: 1,
        }}
      >
        <CardContent sx={{ p: 4 }}>

          {/* Upload Section - Top Row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            {/* Driver Photo */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#344054", mb: 1 }}>
                Driver Photo <span style={{ color: "red" }}>*</span>
              </Typography>
              {isUploadingImage ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : !addDriver.driverImage ? (
                <Box component="label" sx={{ cursor: "pointer", display: "block" }}>
                  <input type="file" accept="image/*" onChange={handleFileChange} hidden />
                  <UploadBox
                    icon={<CloudUploadIcon sx={{ fontSize: 36 }} />}
                    title="Upload Photo"
                    subtitle="JPG, PNG or GIF (Max. 5MB)"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    position: "relative",
                    border: "1px solid #e0e0e0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    p: 1,
                    display: "inline-block",
                  }}
                >
                  <IconButton
                    onClick={handleRemoveDriverImage}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255,255,255,0.9)",
                      "&:hover": { backgroundColor: "#ffebee" },
                      zIndex: 1,
                    }}
                    size="small"
                  >
                    <MdDelete style={{ color: "#ef4444", fontSize: 18 }} />
                  </IconButton>
                  <img
                    src={addDriver.driverImage}
                    alt="Driver"
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Driver License */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#344054", mb: 1 }}>
                Driver License <span style={{ color: "red" }}>*</span>
              </Typography>
              {isUploadingDocument ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : !addDriver.driverDocument ? (
                <Box component="label" sx={{ cursor: "pointer", display: "block" }}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleDocumentUpload}
                    hidden
                  />
                  <UploadBox
                    icon={<InsertDriveFileIcon sx={{ fontSize: 36 }} />}
                    title="Upload License"
                    subtitle="PDF, JPG or PNG (Max. 10MB)"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "12px",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#f8f9fc",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InsertDriveFileIcon sx={{ color: "#7c6dff" }} />
                    <Typography
                      variant="body2"
                      component="a"
                      href={addDriver.driverDocument}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#2196f3",
                        fontWeight: 500,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      View Uploaded License
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleRemoveDriverDocument}
                    size="small"
                    sx={{ "&:hover": { backgroundColor: "#ffebee" } }}
                  >
                    <CloseIcon sx={{ color: "#ef4444", fontSize: 18 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>

          {/* Form Fields Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            {/* Name */}
            {renderField("Name", true,
              <TextField
                fullWidth
                required
                placeholder="Enter full name"
                name="driverName"
                value={addDriver.driverName || ""}
                onChange={handleChange}
                sx={textFieldSx}
              />
            )}

            {/* Email */}
            {renderField("Email", true,
              <TextField
                fullWidth
                required
                type="email"
                placeholder="Enter email address"
                name="email"
                value={addDriver.email || ""}
                onChange={handleChange}
                sx={textFieldSx}
              />
            )}

            {/* Date of Joining */}
            {renderField("Date of Joining", true,
              <TextField
                fullWidth
                required
                placeholder="dd/mm/yyyy"
                type="date"
                name="dateOfJoining"
                value={
                  addDriver.dateOfJoining
                    ? new Date(addDriver.dateOfJoining).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setAddDriver({
                    ...addDriver,
                    dateOfJoining: new Date(e.target.value).getTime(),
                  })
                }
                sx={textFieldSx}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}

            {/* Date of Birth */}
            {renderField("Date of Birth", true,
              <TextField
                fullWidth
                required
                placeholder="dd/mm/yyyy"
                type="date"
                name="age"
                value={
                  addDriver.age
                    ? new Date(addDriver.age).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setAddDriver({
                    ...addDriver,
                    age: new Date(e.target.value).getTime(),
                  })
                }
                sx={textFieldSx}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}

            {/* Phone Number */}
            {renderField("Phone Number", true,
              <TextField
                fullWidth
                required
                placeholder="Enter phone number"
                name="phoneNumber"
                value={addDriver.phoneNumber || ""}
                onChange={handleChange}
                sx={textFieldSx}
              />
            )}

            {/* License Number */}
            {renderField("License Number", true,
              <TextField
                fullWidth
                required
                placeholder="Enter license number"
                name="licenseNo"
                value={addDriver.licenseNo || ""}
                onChange={handleChange}
                sx={textFieldSx}
              />
            )}

            {/* Vehicle Type */}
            {renderField("Vehicle Type", true,
              <TextField
                select
                fullWidth
                required
                name="totalExperiance"
                value={addDriver.totalExperiance || ""}
                onChange={handleChange}
                sx={selectSx}
              >
                <MenuItem value="">Select vehicle type</MenuItem>
                <MenuItem value="Car">Car</MenuItem>
                <MenuItem value="Bus">Bus</MenuItem>
                <MenuItem value="Motorcycle">Motorcycle</MenuItem>
                <MenuItem value="Taxi">Taxi</MenuItem>
              </TextField>
            )}

            {/* Driver Status */}
            {renderField("Driver Status", true,
              <TextField
                select
                fullWidth
                required
                name="driverStatus"
                value={addDriver.driverStatus || "Active"}
                onChange={handleChange}
                sx={selectSx}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Unactive">Inactive</MenuItem>
              </TextField>
            )}

            {/* Address */}
            {renderField("Address", true,
              <GMapAutocomplete
                onLoad={onLoadAutocomplete}
                onPlaceChanged={onPlaceChanged}
                options={{
                  componentRestrictions: { country: "pk" },
                  types: ["geocode", "establishment"],
                  fields: [
                    "address_components",
                    "geometry",
                    "icon",
                    "name",
                    "formatted_address",
                  ],
                }}
              >
                <input
                  type="text"
                  placeholder="Enter complete address"
                  value={locationObject?.streetAddress}
                  style={{
                    width: "100%",
                    height: "46px",
                    padding: "0 14px",
                    borderRadius: "10px",
                    border: "1px solid #d0d5dd",
                    backgroundColor: "#fff",
                    fontSize: "14px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setLocationObject(null);
                      setSelectedPosition(null);
                      setAddDriver((prev: any) => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          streetAddress: "",
                        },
                      }));
                    } else {
                      setLocationObject((prev: any) => ({
                        ...prev,
                        streetAddress: value,
                      }));
                      setAddDriver((prev: any) => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          streetAddress: value,
                        },
                      }));
                    }
                  }}
                />
              </GMapAutocomplete>
            )}

            {/* Emergency Contact */}
            {renderField("Emergency Contact", false,
              <TextField
                fullWidth
                placeholder="Enter emergency contact number"
                name="reference"
                value={addDriver.reference || ""}
                onChange={handleChange}
                sx={textFieldSx}
              />
            )}

            {/* Company */}
            {renderField("Company", true,
              <TextField
                select
                fullWidth
                required
                value={addDriver.company?.companyId || ""}
                onChange={handleChangeForCompany}
                sx={selectSx}
              >
                <MenuItem value="">Select company</MenuItem>
                {allCompany.map((company: any) => (
                  <MenuItem key={company._id} value={company._id}>
                    {company.companyName}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          {/* Map Section */}
          <Box sx={{ mt: 3, borderRadius: "12px", overflow: "hidden", border: "1px solid #e0e0e0" }}>
            <GoogleMap
              center={selectedPosition || { lat: 24.8607, lng: 67.0011 }}
              zoom={selectedPosition ? 14 : 10}
              mapContainerStyle={{ width: "100%", height: "300px" }}
            >
              {selectedPosition && <Marker position={selectedPosition} />}
            </GoogleMap>
          </Box>

          {/* Submit Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                )
              }
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1.2,
                backgroundColor: "#2563EB",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              {loading
                ? isEdit
                  ? "Editing..."
                  : "Adding..."
                : isEdit
                ? "Edit Driver"
                : "Add Driver"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Addriver;
